import { Component, EventEmitter, Input, Output, Renderer2, ViewEncapsulation } from '@angular/core';
import { HtmlPreviewService } from '@fi-shared/fi-print-download/html-preview.service';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { isIOS } from '@fi-shared/utils/isIOS';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';
import { FiImageSize } from '@fi-shared/types';

type SelectorFunction = () => string;

export const FiPrintDownloadNoPageBreak = 'no-print-page-break';

@Component({
    selector: 'fi-print-download',
    template: `
        @if (!isNewLayout) {
          <span class="btn-group">
            @if (!IsIOS && isPrintButtonShown) {
              <button
                class="btn btn-default printCcmIcon"
                (click)="Print()"
                [attr.title]="'common.print' | fiAppText"
              ></button>
            }
            <button
              class="btn btn-default downloadCcmIcon"
              (click)="Download()"
              [attr.title]="'common.download' | fiAppText"
            ></button>
            <ng-content></ng-content>
          </span>
        } @else {
          @if (!IsIOS && isPrintButtonShown) {
            <button
              mat-icon-button
              class="BrandedColor"
              (click)="Print()"
              [attr.title]="'common.print' | fiAppText"
              >
              <mat-icon class="fas fa-print"></mat-icon>
            </button>
          }
          <button
            mat-icon-button
            class="BrandedColor"
            (click)="Download()"
            [attr.title]="'common.download' | fiAppText"
            >
            <mat-icon class="fas fa-download"></mat-icon>
          </button>
          <ng-content></ng-content>
        }
        `,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./fi-print-download.component.scss'],
    host: { class: 'fi-print-download' },
    standalone: false,
})
export class FiPrintDownloadComponent {
    @Input() printElementSelector: string | SelectorFunction;
    @Input() externalGenerateHtml: () => string;
    @Input() downloadFilename: string;
    @Input() isPrintButtonShown: boolean = true;
    @Input() isNewLayout: boolean = false;
    @Input() isCustomDownload: boolean = false;

    @Output() onDownloadButtonClick = new EventEmitter<void>();
    @Output() onGeneratingPdfHtml = new EventEmitter<boolean>();

    constructor(
        private htmlPreviewService: HtmlPreviewService,
        private fiPrintDownloadService: FiPrintDownloadService,
        private fiBackend: FiBackendService,
        private fiLoading: LoadingService,
        private renderer: Renderer2,
    ) {}

    get IsIOS() {
        // do not show "Print" button on iOS because iOS doesn't run actions from pdf file
        // https://bitbucket.org/Chainformation/ccm2-frontend/issues/629/print-and-pdf-functionality-in#comment-25411316
        // https://bitbucket.org/Chainformation/ccm2-frontend/issues/629/print-and-pdf-functionality-in#comment-26963673
        return isIOS();
    }

    public Print(): void {
        this.onGeneratingPdfHtml.next(true);
        setTimeout(() => {
            const formElement: HTMLFormElement = this.fiPrintDownloadService.getFormPrintElement(
                this.fiBackend.getRequestUrl('MediaBank/FileUpload/PrintPdf'),
            );
            formElement.append(this.fiPrintDownloadService.getInputElement(this.generatePdfHtml(), 'Source'));
            formElement.append(this.fiPrintDownloadService.getInputElement(`${this.downloadFilename}.pdf`, 'Name'));
            document.body.appendChild(formElement);
            formElement.submit();
            formElement.remove();
            this.onGeneratingPdfHtml.next(false);
        });
    }

    public Download(args?: { downloadDelayMs?: number }): Promise<void> {
        if (!args) {
            args = {};
        }

        if (this.isCustomDownload) {
            this.onDownloadButtonClick.emit();
            return Promise.resolve();
        }

        this.onGeneratingPdfHtml.next(true);
        const promise = new Promise<void>((resolve, reject) => {
            setTimeout(() => {
                this.generatePdf().then((data: string) => {
                    const promise = this.fiPrintDownloadService
                        .downloadBinaryFile(this.getPdfUrl(data), {
                            fileName: `${this.downloadFilename}.pdf`,
                            size: FiImageSize.Original,
                            getMethod: true,
                        })
                        .then(resolve, reject);
                }, reject);
            }, args.downloadDelayMs ?? 0);
        }).finally(() => {
            this.onGeneratingPdfHtml.next(false);
        });
        return this.fiLoading.trackPromise(promise);
    }

    private generatePdfHtml(): string {
        const printElements: HTMLElement[] = [];
        if (this.printElementSelector)
            printElements.push(...Array.from(document.querySelectorAll<HTMLElement>(this.getPrintElementSelector())));

        const HTMLElements: string[] = printElements.map((el: HTMLElement) =>
            this.htmlPreviewService.getPreviewHtml(el, undefined, (layout) => {
                this.renderer.setStyle(layout, 'margin-left', '50px');
            }),
        );

        let pdfHTML: string = '';
        for (let i = 0; i < HTMLElements.length; i++) {
            pdfHTML += HTMLElements[i];
            if (i !== HTMLElements.length - 1 && !printElements[i].classList.contains(FiPrintDownloadNoPageBreak))
                pdfHTML += '<div style="page-break-before: always;"></div>';
        }
        return pdfHTML;
    }

    private getPrintElementSelector(): string {
        return typeof this.printElementSelector === 'function'
            ? this.printElementSelector()
            : this.printElementSelector;
    }

    private generatePdf(): IFiHttpPromise<string> {
        const pdfHtml: string = this.externalGenerateHtml ? this.externalGenerateHtml() : undefined;
        return this.fiPrintDownloadService.generatePdf(pdfHtml || this.generatePdfHtml());
    }

    private getPdfUrl(physicalFileName: string) {
        return (
            'MediaBank/FileUpload/DownloadGeneratedFile?physicalFileName=' +
            encodeURIComponent(physicalFileName) +
            '&fileName=' +
            encodeURIComponent(this.downloadFilename) +
            '.pdf'
        );
    }
}
